import { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/user-dashboard/api';
import { DashboardStat } from '@/api/user-dashboard/type';

interface StatItem {
    label: string;
    value: string;
    unit: string;
    sub: [string, string?];
    icon: string;
    color: string;
    trend: string;
    trendUp: boolean;
    emptyMessage: string;
}

const AgencyStatCards = () => {
    const [stats, setStats] = useState<StatItem[]>([]);
    const [loading, setLoading] = useState(true);

    const buildStats = (data: DashboardStat): StatItem[] => [
        {
            label: '작기수',
            value: String(data.seasonStat.totalSeasonCnt ?? 0),
            unit: '작기',
            sub: [
                `진행중 ${data.seasonStat.activeSeasonCnt ?? 0}`,
                `완료 ${data.seasonStat.inactiveSeasonCnt ?? 0}`,
            ],
            icon: 'ri-calendar-check-line',
            color: 'success',
            trend: `${data.seasonStat.monthDiffCnt > 0 ? '+' : ''}${data.seasonStat.monthDiffCnt ?? 0}`,
            trendUp: (data.seasonStat.monthDiffCnt ?? 0) >= 0,
            emptyMessage: '작기를 등록해주세요.',
        },
        {
            label: '농지수',
            value: String(data.farmlandStat.totalFarmlandCnt ?? 0),
            unit: '필지',
            sub: [
                `관리중 ${data.farmlandStat.managedCnt ?? 0}`,
                `휴경 ${data.farmlandStat.unmanagedCnt ?? 0}`,
            ],
            icon: 'ri-landscape-line',
            color: 'primary',
            trend: `${data.farmlandStat.monthDiffCnt > 0 ? '+' : ''}${data.farmlandStat.monthDiffCnt ?? 0}`,
            trendUp: (data.farmlandStat.monthDiffCnt ?? 0) >= 0,
            emptyMessage: '농지를 등록해주세요.',
        },
        {
            label: '농지면적',
            value: `${String(data.areaStat.totalArea ?? 0)}`,
            unit: '㎡',
            sub: [`${data.areaStat.totalAreaPyeong ?? 0} 평`],
            icon: 'ri-map-2-line',
            color: 'info',
            trend: `${(data.areaStat.monthDiffArea ?? 0) > 0 ? '+' : ''}${data.areaStat.monthDiffArea ?? 0} ㎡`,
            trendUp: (data.areaStat.monthDiffArea ?? 0) >= 0,
            emptyMessage: '면적 정보가 없습니다.',
        },
        {
            label: '재배작물수',
            value: String(data.cropStat.totalCropCnt ?? 0),
            unit: '품종',
            sub: [
                `최다작물 - ${data.cropStat.mostUsedCropName ?? '-'}`,
                `전체 ${data.cropStat.totalCropCnt ?? 0}종`,
            ],
            icon: 'ri-plant-line',
            color: 'warning',
            trend: `${(data.cropStat.monthDiffCnt ?? 0) > 0 ? '+' : ''}${data.cropStat.monthDiffCnt ?? 0}`,
            trendUp: (data.cropStat.monthDiffCnt ?? 0) >= 0,
            emptyMessage: '작물을 등록해주세요.',
        },
    ];

    const getDashboardStat = useCallback(async () => {
        try {
            setLoading(true);
            const result = await api.dashboardStat();
            setStats(buildStats(result));
        } catch (e) {
            console.error('대시보드 통계 조회 실패', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        getDashboardStat();
    }, [getDashboardStat]);

    /** value가 "0" 또는 "0 / 0" 처럼 숫자 합산이 0인 경우를 빈 상태로 판단 */
    const isEmpty = (value: string) => {
        const nums = value.match(/[\d.]+/g);
        if (!nums) return true;
        return nums.every(n => parseFloat(n) === 0);
    };

    if (loading) {
        return (
            <div className="row g-3">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div className="col-12 col-sm-6 col-md-3" key={i}>
                        <div className="card custom-card placeholder-glow">
                            <div className="card-body">
                                <span
                                    className="placeholder col-12 rounded"
                                    style={{ height: 80 }}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="row g-3">
            {stats.map((stat, i) => {
                const empty = isEmpty(stat.value);

                if (empty) {
                    return (
                        <div className="col-12 col-sm-6 col-md-3" key={i}>
                            <div
                                className="card custom-card"
                                style={{
                                    border: '2px dashed #ced4da',
                                    background: '#f8f9fa',
                                    minHeight: 110,
                                }}
                            >
                                <div
                                    className="card-body d-flex flex-column align-items-center justify-content-center gap-2"
                                    style={{ color: '#adb5bd' }}
                                >
                                    <i
                                        className={`${stat.icon} fs-28`}
                                        style={{ opacity: 0.4 }}
                                    />
                                    <p
                                        className="mb-0 fw-medium text-center"
                                        style={{
                                            fontSize: 13,
                                            color: '#868e96',
                                        }}
                                    >
                                        {stat.emptyMessage}
                                    </p>
                                </div>
                            </div>
                        </div>
                    );
                }

                return (
                    <div className="col-12 col-sm-6 col-md-3" key={i}>
                        <div
                            className={`card custom-card hrm-main-card ${stat.color}`}
                        >
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="mb-2 fs-14">
                                            {stat.label}
                                        </p>
                                        <h4 className="mb-2 fw-semibold">
                                            {stat.value}
                                            <small
                                                className="text-muted fw-normal ms-1"
                                                style={{ fontSize: 13 }}
                                            >
                                                {stat.unit}
                                            </small>
                                        </h4>
                                        <div>
                                            <div className="d-flex gap-1 align-items-center">
                                                {stat.sub.map((text, j) => (
                                                    <span
                                                        key={j}
                                                        className={`badge bg-${stat.color}-transparent text-${stat.color}`}
                                                        style={{ fontSize: 11 }}
                                                    >
                                                        <i className="ri-information-line me-1"></i>
                                                        {text}
                                                    </span>
                                                ))}
                                                <span className="text-muted fs-7 ms-2 me-2">
                                                    |
                                                </span>
                                                <div>
                                                    <span className="fw-medium fs-12 text-muted">
                                                        전월대비
                                                    </span>
                                                    <span
                                                        className={`badge rounded-pill bg-${stat.trendUp ? 'success' : 'danger'}-transparent text-${stat.trendUp ? 'success' : 'danger'} fs-11`}
                                                    >
                                                        <i
                                                            className={`ri-arrow-${stat.trendUp ? 'up' : 'down'}-s-fill`}
                                                        ></i>
                                                        {stat.trend}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <span
                                            className={`avatar avatar-lg rounded-4 bg-${stat.color} shadow shadow-${stat.color} text-fixed-white`}
                                        >
                                            <i
                                                className={`${stat.icon} fs-21`}
                                            ></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AgencyStatCards;
